import { Col, Row } from 'antd';
import {
  ApplicantPreviewBlock,
  BillingPreviewBlock,
  ComplementingFactsPreviewBlock,
  DateAndTimePreviewBlock,
  ManagersPreviewBlock,
  MapLegendBlock,
  MotivationPreviewBlock,
  PaddingBlock,
  PreviewBlock,
  TaPlanInfoPreviewBlock,
  WorkTypePreviewBlock
} from 'lib/Antd/Preview';
import { types } from 'mobx-state-tree';
import styled from 'styled-components';
import { CaseStatus, Panel, Step } from 'utils/enumerations';
import { panelHasAnyValue, taPlanPanelOrderList, taPlanStepPanels, tapPanelWithKeys } from 'utils/panelUtils';
import { ApplicationBaseModel } from './applicationBaseModel';
import { CaseInfoTaPlanBlockModel } from './blocks/caseinfoTaPlanBlockModel';
import { ManagerBlockModel } from './blocks/managerBlockModel';
import { TARegulationPoint, TARegulationPolygon, TARegulationPolyline } from './geometry';
import { RoadSignRegulationModel } from './roadSignRegulationModel';

const CompletePage = styled.div`
  height: 22.3cm;
`;

const TAPlanSpecificModel = types
  .model('TAPlanSpecificModel', {
    projectNumber: types.maybe(types.string),
    caseInfo: types.optional(CaseInfoTaPlanBlockModel, {}),
    workType: types.maybe(types.string),
    prolongedTo: types.maybe(types.string),
    locationDescription: types.maybe(types.string),
    roadSignRegulations: types.array(RoadSignRegulationModel),
    newEndTime: types.maybe(types.string),
    managers: types.optional(ManagerBlockModel, {}),
    approvalStatement: types.maybe(types.string),
    streetClosed: types.maybe(types.boolean),
    regulationsNeeded: types.maybe(types.boolean),
    locationSpecific: true,
    regulationSnapShot: types.maybe(types.string),
    regulationPoints: types.array(TARegulationPoint),
    regulationPolylines: types.array(TARegulationPolyline),
    regulationPolygons: types.array(TARegulationPolygon),
    prolongedStatus: types.maybe(types.integer),
    prolongedApprovedDate: types.maybe(types.string)
  })
  .views(self => ({
    get name() {
      return self.caseInfo.caseName ? self.caseInfo.caseName : self.workType;
    }
  }));

export const TAPlanModel = types
  .compose(ApplicationBaseModel, TAPlanSpecificModel)
  .named('TAPlanModel')
  .views(self => ({
    getPreview(t) {
      return (
        <>
          {self.status >= CaseStatus.Processed && (
            <CompletePage>
              <TaPlanInfoPreviewBlock
                caseInfo={self.caseInfo}
                caseStatus={self.status}
                currentPanel={self.currentPanel}
                prolongedStatus={self.prolongedStatus}
              />
              <MotivationPreviewBlock
                answer={self.statusMotivation ? self.statusMotivation : self.approvalStatement}
                handlingOfficerName={self.handlingOfficerName}
              />
            </CompletePage>
          )}
          <TaPlanInfoPreviewBlock
            caseInfo={self.caseInfo}
            caseStatus={self.status}
            currentPanel={self.currentPanel}
            prolongedStatus={self.prolongedStatus}
          />
          {self.isPanelTouchedOrHasValue(Panel.Questioneer) && (
            <ApplicantPreviewBlock
              applicant={self.applicant}
              billingInfo={self.billingInformation}
              currentPanel={self.currentPanel}
            />
          )}
          {self.isPanelTouchedOrHasValue(Panel.InvoiceAddress) && (
            <BillingPreviewBlock
              billingInfo={self.billingInformation}
              applicant={self.applicant}
              currentPanel={self.currentPanel}
            />
          )}
          {self.isPanelTouchedOrHasValue(Panel.WorkType) && (
            <WorkTypePreviewBlock workType={self.workType} currentPanel={self.currentPanel} />
          )}
          {self.isPanelTouchedOrHasValue(Panel.DateAndTimePeriod) && (
            <DateAndTimePreviewBlock
              dateTimeInfo={self.dateTimeInformation}
              prolongedTo={self.prolongedTo}
              prolongedStatus={self.prolongedStatus}
              currentPanel={self.currentPanel}
            />
          )}
          {self.isPanelTouchedOrHasValue(Panel.MarkingManager) && (
            <ManagersPreviewBlock
              managers={self.managers}
              currentPanel={self.currentPanel}
              locationSpecific={self.locationSpecific}
            />
          )}
          {self.childCases.length ||
          self.parentCases.length ||
          self.isPanelTouchedOrHasValue(Panel.AdditionalInformation) ? (
            <ComplementingFactsPreviewBlock
              additionalInformation={self.additionalInformation}
              attachments={self.attachments}
              childCases={self.childCases}
              parentCases={self.parentCases}
              currentPanel={self.currentPanel}
              locationSpecific={self.locationSpecific}
            />
          ) : null}
          {self.geometrySnapshot && (
            <CompletePage isMapPage>
              <PreviewBlock step={Step.Map} panel={Panel.Extent}>
                <Col span={24} offset={0}>
                  <PaddingBlock />
                  <Row justify="start">
                    <img
                      style={{ width: '100%' }}
                      className="preview-map"
                      src={self.geometrySnapshot}
                      alt={t('preview:extent')}
                    />
                  </Row>
                </Col>
                <Col span={24} offset={0}>
                  <PaddingBlock />
                  {!self.regulationSnapShot && (
                    <MapLegendBlock
                      taRegulationPoints={self.regulationPoints}
                      taRegulationPolylines={self.regulationPolylines}
                      taRegulationPolygons={self.regulationPolygons}
                    />
                  )}
                </Col>
              </PreviewBlock>
            </CompletePage>
          )}
          {self.regulationSnapShot && (
            <CompletePage isMapPage>
              <PreviewBlock step={Step.Map} panel={Panel.Extent}>
                <Col span={24} offset={0}>
                  <PaddingBlock />
                  <Row justify="start">
                    <img
                      style={{ width: '100%' }}
                      className="preview-map"
                      src={self.regulationSnapShot}
                      alt={t('preview:extent')}
                    />
                  </Row>
                </Col>
                <Col span={24} offset={0}>
                  <PaddingBlock />
                  <MapLegendBlock
                    taRegulationPoints={self.regulationPoints}
                    taRegulationPolylines={self.regulationPolylines}
                    taRegulationPolygons={self.regulationPolygons}
                  />
                </Col>
              </PreviewBlock>
            </CompletePage>
          )}
        </>
      );
    },
    getPanelByKey: itemKey => {
      return tapPanelWithKeys.find(panel => panel.keys.find(panelKey => panelKey === itemKey))?.panelName;
    }
  }))
  .actions(self => ({
    setProlong: (prolongedTo, newEndDate, prolongedStatus) => {
      self.prolongedTo = prolongedTo;
      self.newEndTime = newEndDate;
      self.prolongedStatus = prolongedStatus;
    },
    isPanelTouchedOrHasValue: panel => {
      if (self.status >= CaseStatus.Registered) {
        self.setTouchedPanelIndex(panel);
        return true;
      }
      const currentPanelIndex = taPlanPanelOrderList.findIndex(touchedPanel => touchedPanel === panel);
      if (currentPanelIndex <= self.touchedPanelIndex) {
        return true;
      } else if (panelHasAnyValue(self.toJSON(), panel, tapPanelWithKeys)) {
        self.setTouchedPanelIndex(panel);
        return true;
      }
      return false;
    },
    addTAGeometry: (geometryType, graphic) => {
      if (geometryType && graphic) {
        self.geometriesHasChanged = true;
        const graphicToAdd = {
          ...graphic.attributes,
          objectType: parseInt(graphic.attributes.objectType)
        };
        switch (geometryType) {
          case 'point':
            graphicToAdd.point = graphic.geometry.toJSON();
            self.regulationPoints.push(graphicToAdd);
            break;
          case 'polyline':
            graphicToAdd.polyline = graphic.geometry.toJSON();
            self.regulationPolylines.push(graphicToAdd);
            break;
          case 'polygon':
            graphicToAdd.polygon = graphic.geometry.toJSON();
            self.regulationPolygons.push(graphicToAdd);
            break;
          default:
            break;
        }
      }
    },
    updateTaGeometry: (geometryType, graphic) => {
      switch (geometryType) {
        case 'point':
          const point = self.regulationPoints.find(geo => geo.gid === graphic.attributes.gid);
          if (point) {
            point.update(graphic);
          }
          break;
        case 'polyline':
          const polyline = self.regulationPolylines.find(geo => geo.gid === graphic.attributes.gid);
          if (polyline) {
            polyline.update(graphic);
          }
          break;
        case 'polygon':
          const polygon = self.regulationPolygons.find(geo => geo.gid === graphic.attributes.gid);
          if (polygon) {
            polygon.update(graphic);
          }
          break;
        default:
          break;
      }
      self.geometriesHasChanged = true;
    },
    setGeometryHasChanged: value => {
      self.geometriesHasChanged = value;
    },
    deleteAllTAGeometries: () => {
      self.regulationPoints = [];
      self.regulationPolylines = [];
      self.regulationPolygons = [];
      self.geometriesHasChanged = true;
    },
    deleteRegulationSnapshot: () => {
      self.regulationSnapShot = undefined;
    },
    removeTAGeometry: (geometryType, gid) => {
      let removeSuccessful = false;
      switch (geometryType) {
        case 'point':
          const pointIndex = self.regulationPoints.findIndex(geo => geo.gid === gid);
          if (pointIndex !== -1) {
            self.regulationPoints.splice(pointIndex, 1);
            removeSuccessful = true;
          }
          break;
        case 'polyline':
          const polylineIndex = self.regulationPolylines.findIndex(geo => geo.gid === gid);
          if (polylineIndex !== -1) {
            self.regulationPolylines.splice(polylineIndex, 1);
            removeSuccessful = true;
          }
          break;
        case 'polygon':
          const polygonIndex = self.regulationPolygons.findIndex(geo => geo.gid === gid);
          if (polygonIndex !== -1) {
            self.regulationPolygons.splice(polygonIndex, 1);
            removeSuccessful = true;
          }
          break;
        default:
          break;
      }
      if (removeSuccessful) {
        self.geometriesHasChanged = true;
      }
      return removeSuccessful;
    },
    setTouchedPanelIndex: panel => {
      const currentPanelIndex = taPlanPanelOrderList.findIndex(touchedPanel => touchedPanel === panel);
      if (self.touchedPanelIndex < currentPanelIndex) {
        self.touchedPanelIndex = currentPanelIndex;
        const step = taPlanStepPanels.find(steps => steps.panels.find(panelName => panel === panelName))?.step;
        self.setTouchedStep(step);
      }
    },
    setSwitchValues(changedValues) {
      for (let key in changedValues) {
        self[key] = changedValues[key];
      }
    }
  }));
